import { ApiBase } from "../../core/api-base";
import type * as z from "zod";
import { GetSearchMenu200Response } from "../../api/generated/search.zod";
import { GetItems200Response as GetProducts200Response } from "../../api/generated/products.zod";
import { GetItem200Response as GetUser200Response } from "../../api/generated/users.zod";

export type SearchMenuPrimitive = string | number | boolean | null | undefined;
export type SearchMenuRecord = Record<string, unknown>;
type SearchMenuApiResponse = z.infer<typeof GetSearchMenu200Response>;
type SearchMenuProductsResponse = z.infer<typeof GetProducts200Response>;
type SearchMenuUserLookupResponse = z.infer<typeof GetUser200Response>;

export type SearchMenuData = NonNullable<SearchMenuApiResponse["data"]>;
type SearchMenuInput =
    | SearchMenuPrimitive
    | SearchMenuRecord
    | SearchMenuData
    | SearchMenuApiResponse
    | SearchMenuUserLookupResponse
    | null
    | undefined;

interface SearchMenuCacheData {
    data: SearchMenuData;
    timestamp: number;
}

interface SearchMenuUserLabel {
    id: string;
    name: string;
}

function isRecord(value: unknown): value is SearchMenuRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export class SearchMenuApi extends ApiBase {
    public apiEndpoint: string;
    public cacheKey: string;
    public cacheExpiry: number;
    public cachedData: SearchMenuData | null;
    public cacheTimestamp: number | null;
    private _userLabelCache: Map<string, string>;
    private _userLabelPromiseById: Map<string, Promise<string | null>>;

    public constructor() {
        super();
        this.apiEndpoint = "/api/v1/search/menu";
        this.cacheKey = "searchMenuData.v2";
        this.cacheExpiry = 10 * 60 * 1000;
        this.cachedData = null;
        this.cacheTimestamp = null;

        this._userLabelCache = this.createUserLabelCache();
        this._userLabelPromiseById = this.createUserLabelPromiseByIdCache();
    }

    public createUserLabelCache(): Map<string, string> {
        return new Map();
    }

    public createUserLabelPromiseByIdCache(): Map<string, Promise<string | null>> {
        return new Map();
    }

    public async saveSearchHistory(
        userId: number,
        query: string,
        searchType: string,
        sectionId = "products",
    ): Promise<void> {
        try {
            await super.apiPost("/api/v1/search/history", {
                user_id: userId,
                query,
                search_type: searchType,
                section_id: sectionId,
            });
        } catch (error) {
            console.error("Failed to save search history:", error);
        }
    }

    public async getSearchMenu(): Promise<SearchMenuData> {
        if (this.isCacheValid()) {
            if (this.isValidMenuData(this.cachedData)) {
                return this.cachedData;
            }

            this.clearCache();
        }

        try {
            const response = await super.apiGet(this.apiEndpoint, { noSpinner: true });
            const result = (await response.json()) as SearchMenuApiResponse;

            if (result.success && result.data) {
                if (!this.isValidMenuData(result.data)) {
                    console.error("[SearchMenuApi] Invalid search menu payload shape");
                    return this.getEmptyData();
                }

                this.cachedData = result.data;
                this.cacheTimestamp = Date.now();
                this.saveToLocalStorage();
                return result.data;
            }

            console.error("[SearchMenuApi] Failed to load search menu:", result.message || "Unexpected error");

            const local = this.getFromLocalStorage();
            return this.isValidMenuData(local) ? local : this.getEmptyData();
        } catch (error) {
            console.error("[SearchMenuApi] API error:", error);
            const local = this.getFromLocalStorage();
            return this.isValidMenuData(local) ? local : this.getEmptyData();
        }
    }

    public async searchProductsBySelections(params: URLSearchParams): Promise<SearchMenuProductsResponse> {
        const url = `/api/v1/products?${params.toString()}`;
        const response = await super.apiGet(url, { noSpinner: true });
        return (await response.json().catch(() => ({}))) as SearchMenuProductsResponse;
    }

    /**
     * The brand's product_status=brand placeholder product (ISS-02-209) —
     * null if the brand doesn't exist or genuinely has none (shouldn't
     * normally happen, see BrandPlaceholderProductHelper).
     */
    public async fetchBrandPlaceholderProduct(brandId: string): Promise<SearchMenuRecord | null> {
        try {
            const response = await super.apiGet(`/api/v1/brands/${encodeURIComponent(brandId)}/placeholder-product`, {
                noSpinner: true,
            });
            if (!response.ok) return null;
            const json = (await response.json().catch(() => null)) as { data?: unknown } | null;
            return isRecord(json?.data) ? json.data : null;
        } catch (_error) {
            return null;
        }
    }

    public isValidMenuData(data: SearchMenuInput): data is SearchMenuData {
        if (!isRecord(data)) {
            return false;
        }

        if ("parent_categories" in data && Array.isArray(data.parent_categories)) {
            return true;
        }

        return "categories" in data && Array.isArray(data.categories);
    }

    public isCacheValid(): boolean {
        if (!this.cachedData || !this.cacheTimestamp) {
            return false;
        }

        const age = Date.now() - this.cacheTimestamp;
        return age < this.cacheExpiry;
    }

    public saveToLocalStorage(): void {
        try {
            const cacheData: SearchMenuCacheData = {
                data: this.cachedData ?? this.getEmptyData(),
                timestamp: this.cacheTimestamp ?? Date.now(),
            };
            localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.warn("[SearchMenuApi] Failed to save to localStorage:", error);
        }
    }

    public getFromLocalStorage(): SearchMenuData | null {
        try {
            const cached = localStorage.getItem(this.cacheKey);
            if (!cached) return null;

            const cacheData = JSON.parse(cached) as SearchMenuCacheData;
            const age = Date.now() - cacheData.timestamp;

            if (age < this.cacheExpiry) {
                this.cachedData = cacheData.data;
                this.cacheTimestamp = cacheData.timestamp;
                return cacheData.data;
            }

            localStorage.removeItem(this.cacheKey);
            return null;
        } catch (error) {
            console.warn("[SearchMenuApi] Failed to read from localStorage:", error);
            return null;
        }
    }

    public getEmptyData(): SearchMenuData {
        return {
            parent_categories: [],
            categories: [],
            popular_brands: [],
            all_brands: [],
            popular_products: [],
            popular_keywords: [],
        };
    }

    public clearCache(): void {
        this.cachedData = null;
        this.cacheTimestamp = null;
        try {
            localStorage.removeItem(this.cacheKey);
        } catch (error) {
            console.warn("[SearchMenuApi] Failed to clear localStorage:", error);
        }
    }

    public async resolveUserLabelsByIds(ids: string[]): Promise<Map<string, string>> {
        const uniqueIds = Array.from(new Set((ids || []).map((value) => String(value ?? "").trim()).filter(Boolean)));
        const map = new Map<string, string>();
        if (uniqueIds.length === 0) return map;

        const toUserLabel = (
            json: SearchMenuUserLookupResponse | SearchMenuRecord | null,
        ): SearchMenuUserLabel | null => {
            const jsonObj = isRecord(json) ? json : null;
            const data = isRecord(jsonObj?.data) ? jsonObj.data : null;
            const legacyJsonObj = jsonObj as SearchMenuRecord | null;
            const item = isRecord(data?.item) ? data.item : isRecord(legacyJsonObj?.item) ? legacyJsonObj.item : null;
            const raw: SearchMenuRecord | null = item
                ? (item as SearchMenuRecord)
                : jsonObj && !("data" in jsonObj) && !("item" in jsonObj)
                  ? jsonObj
                  : null;
            if (!raw) return null;

            const id = String(raw.user_id ?? raw.id ?? "").trim();
            if (!id) return null;

            const name = String(
                raw.display_name ?? raw.nick_name ?? raw.full_name ?? raw.name ?? raw.email ?? id,
            ).trim();
            return { id, name: name || id };
        };

        await Promise.all(
            uniqueIds.map(async (id) => {
                const cached = this._userLabelCache.get(id);
                if (cached) {
                    map.set(id, cached);
                    return;
                }

                const existingPromise = this._userLabelPromiseById.get(id);
                const promise =
                    existingPromise ||
                    (async () => {
                        try {
                            const response = await super.apiGet(`/api/v1/users/${encodeURIComponent(id)}`, {
                                noSpinner: true,
                            });
                            const json = (await response.json()) as SearchMenuUserLookupResponse;
                            const user = toUserLabel(json);
                            if (!user) return null;
                            return String(user.name || user.id);
                        } catch (_error) {
                            return null;
                        }
                    })();

                if (!existingPromise) {
                    this._userLabelPromiseById.set(id, promise);
                }

                const label = await promise;
                if (!existingPromise) {
                    this._userLabelPromiseById.delete(id);
                }
                if (!label) return;

                this._userLabelCache.set(id, label);
                map.set(id, label);
            }),
        );

        return map;
    }
}
